import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaWhatsapp, FaArrowRight, FaRocket, FaCode, FaTrophy, FaUsers, FaStar, FaCalendarAlt, FaMapMarkerAlt } from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import PDFNewsletterPopup from './PDFNewsletterPopup';
import './HomePage.css';

const useCounter = (target, duration = 2000, start = false) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
};

const testimonials = [
  {
    id: 1,
    name: "David Gideon Kariuki HSC",
    role: "Merishaw School Principal",
    text: "Robots make creativity easier and show our children are conquering the world. STEMTRIX has transformed how our students engage with learning.",
    initial: "D"
  },
  {
    id: 2,
    name: "Sarah Kimani",
    role: "Parent",
    text: "My daughter's confidence in STEM subjects has grown tremendously. She's now seriously considering engineering as a career — something I never imagined a year ago.",
    initial: "S"
  },
  {
    id: 3,
    name: "Isaac Arabu",
    role: "Robotics Coach",
    text: "The curriculum and materials provided by STEMTRIX make teaching robotics accessible and genuinely exciting for both educators and students alike.",
    initial: "I"
  }
];

const news = [
  {
    id: 1,
    date: "March 6/7, 2026",
    title: "Inspire Robotics Challenge 2026",
    excerpt: "Schools competed at St. Andrews School under the theme Climate. Winners will represent Kenya in Beijing, China.",
    video: "https://www.youtube.com/embed/T-PeDh3SCAo",
    link: "/news/inspire-robotics-challenge-2026"
  },
  {
    id: 2,
    date: "August, 2025",
    title: "RobotChallenge 2025",
    excerpt: "Our young innovators represented with confidence, creativity and teamwork at the 2025 Robot Challenge in China. Watching students turn ideas into real solutions is the future we believe in.",
    video: "https://www.youtube.com/embed/WTpod7xt8Ps",
    link: "/news/RobotChallenge-2025"
  },




  {
    id: 1,
    date: "March 15, 2025",
    title: "Inspire Robotics Challenge 2025",
    excerpt: "Schools competed at Potterhouse Senior School under the theme Health. Winners will represent Kenya in Beijing, China.",
    video: "https://www.youtube.com/embed/eV7RRlNloZs",
    link: "/news/inspire-robotics-challenge-2025"
  },
  {
    id: 2,
    date: "April 5, 2025",
    title: "Robofest at Merishaw School",
    excerpt: "Seven schools competed across multiple robotics challenges. Top teams earned spots at the global Robofest event in Michigan.",
    video: "https://www.youtube.com/embed/8P7GzmF8Xf0",
    link: "/news/robofest-merishaw-school"
  },
  {
    id: 3,
    date: "February 10, 2025",
    title: "Inspire Robotics Champions 2023/24",
    excerpt: "Riara Springs Girls High School showcased outstanding innovation and teamwork to claim the championship title.",
    video: "https://www.youtube.com/embed/cfNnN_UxdpQ",
    link: "/news/inspire-robotics-champions-2024"
  },
  {
    id: 4,
    date: "January 15, 2025",
    title: "2023–2024 Robotics Season",
    excerpt: "A season recap of innovative challenges, breakthrough builds, and the young engineers who made it all happen.",
    video: "https://www.youtube.com/embed/RKgyaGQUqsE",
    link: "/news/robotics-season-2023-2024"
  }
];

const HomePage = () => {
  const [whatsappVisible, setWhatsappVisible] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef(null);

  const schools     = useCounter(40,   2000, statsVisible);
  const students    = useCounter(2000, 2200, statsVisible);
  const competitions= useCounter(12,   1800, statsVisible);
  const years       = useCounter(8,    1600, statsVisible);

  useEffect(() => {
    const handleScroll = () => setWhatsappVisible(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Helmet>
        <title>STEMTRIX — Robotics &amp; Coding Education for Kids age 4–17 years old</title>
        <meta name="description" content="STEMTRIX empowers young Kenyan minds through world-class robotics and  coding education. Join our school programmes, Innovation centre and holiday camps across Kenya." />
        <meta name="keywords" content="robotics education Kenya, kids coding Kenya, STEM school programme, summer robotics camp Nairobi, Coding for kids Kenya, STEMTRIX, robotics competition Kenya, children coding classes" />
        <meta property="og:title" content="STEMTRIX — Robotics & Coding for Kids in Kenya" />
        <meta property="og:description" content="Hands-on robotics and coding programmes for children aged 4–17. School programmes and holiday camps across Kenya." />
        <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=DM+Sans:wght@400;500;600&display=swap" rel="stylesheet" />
      </Helmet>
      <PDFNewsletterPopup />

      <main className="stx-home">

        {/* ── HERO ── */}
        <section className="stx-hero">
          <div className="stx-hero__bg-shapes" aria-hidden="true">
            <div className="stx-shape stx-shape--circle-lg" />
            <div className="stx-shape stx-shape--circle-sm" />
            <div className="stx-shape stx-shape--bar-1" />
            <div className="stx-shape stx-shape--bar-2" />
          </div>

          <div className="stx-container stx-hero__inner">
            <div className="stx-hero__content">
              <span className="stx-eyebrow">Robotics &amp; Coding Education · Kenya</span>
              <h1 className="stx-hero__title">
                Building Tomorrow's<br />
                <em className="stx-hero__title-em">Innovators</em> Today
              </h1>
              <p className="stx-hero__subtitle">
                We bring hands-on robotics and coding programs into schools across Kenya — sparking curiosity, building real skills, and preparing students for a technology-driven future.
              </p>
              <div className="stx-hero__cta">
                <Link to="/join-us" className="stx-btn stx-btn--red">
                  Register Your Child<FaArrowRight />
                </Link>
                <Link to="/service" className="stx-btn stx-btn--outline-white">
                  View Our Services
                </Link>

                 <Link to="/programs" className="stx-btn stx-btn--red">
                  Explore Our Programs<FaArrowRight />
                </Link>
              </div>
            </div>

            <div className="stx-hero__visual" aria-hidden="true">
              <div className="stx-robo">
                <div className="stx-robo__head">
                  <div className="stx-robo__eye stx-robo__eye--left" />
                  <div className="stx-robo__eye stx-robo__eye--right" />
                  <div className="stx-robo__mouth" />
                </div>
                <div className="stx-robo__body">
                  <div className="stx-robo__screen">
                    <span className="stx-robo__code">if ready:</span>
                    <span className="stx-robo__code stx-robo__code--indent">build()</span>
                    <span className="stx-robo__code stx-robo__code--indent">innovate()</span>
                    <span className="stx-robo__cursor" />
                  </div>
                </div>
                <div className="stx-robo__arm stx-robo__arm--left" />
                <div className="stx-robo__arm stx-robo__arm--right" />
                <div className="stx-robo__legs">
                  <div className="stx-robo__leg" />
                  <div className="stx-robo__leg" />
                </div>
              </div>
            </div>
          </div>

          <div className="stx-hero__scroll-cue" aria-hidden="true">
            <div className="stx-hero__scroll-line" />
          </div>
        </section>

        {/* ── STATS STRIP ── */}
        <section className="stx-stats" ref={statsRef}>
          <div className="stx-container stx-stats__inner">
            {[
              { value: schools,      suffix: '+',    label: 'Partner Schools' },
              { value: students,     suffix: '+',    label: 'Students Reached' },
              { value: competitions, suffix: '',     label: 'Competitions Held' },
              { value: years,        suffix: ' yrs', label: 'Years of Impact' },
            ].map((stat, i) => (
              <div key={i} className="stx-stat">
                <span className="stx-stat__value">{stat.value}{stat.suffix}</span>
                <span className="stx-stat__label">{stat.label}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ── SUMMER CAMP ADVERT ── */}
        <section className="stx-camp-advert" aria-label="Summer Robotics Camp advertisement">
          <div className="stx-camp-advert__bg-lines" aria-hidden="true">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="stx-camp-advert__line" />
            ))}
          </div>
          <div className="stx-container stx-camp-advert__inner">
            <div className="stx-camp-advert__badge">
              <FaStar /> Limited Spots — Book Now
            </div>
            <div className="stx-camp-advert__content">
              <div className="stx-camp-advert__text">
                <span className="stx-eyebrow stx-eyebrow--white">Holiday Robotics and Coding Camp · Ages 4–17</span>
                <h2 className="stx-camp-advert__title">
                  Give Your Child a Summer<br />They'll Never Forget
                </h2>
                <p className="stx-camp-advert__desc">
                  This holiday, your child will explore robotics and coding, including Python programming, by building and programming real robots at STEMTRIX — gaining hands-on STEM skills in a fun, safe, and inspiring environment.
                </p>
                <ul className="stx-camp-advert__features">
                  <li><FaRocket /> Design &amp; build real robots from scratch</li>
                  <li><FaCode />   Learn Python and block-based coding</li>
                  <li><FaTrophy /> Mini competitions &amp; showcase events</li>
                  <li><FaUsers />  Small groups — maximum attention per child</li>
                </ul>
                <div className="stx-camp-advert__meta">
                  <span><FaCalendarAlt /> School Holidays 2026 </span>
                  <span><FaMapMarkerAlt /> Nairobi, Kenya</span>
                  <span className="stx-camp-advert__ages">Ages 4 – 17</span>
                </div>
                <div className="stx-camp-advert__cta">
                  <Link to="/join-us" className="stx-btn stx-btn--white">
                    Secure Your Child's Spot <FaArrowRight />
                  </Link>
                  <a
                    href="https://wa.me/+254728128353?text=Hello!%20I%20would%20like%20to%20enrol%20my%20child%20in%20the%20STEMTRIX%20Summer%20Robotics%20Camp."
                    className="stx-btn stx-btn--outline-white"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaWhatsapp /> Ask on WhatsApp
                  </a>
                </div>
              </div>
              <div className="stx-camp-advert__card" aria-hidden="true">
                <div className="stx-camp-card">
                  <div className="stx-camp-card__header">
                    <span className="stx-camp-card__tag">STEMTRIX Camp 2025</span>
                    <div className="stx-camp-card__robo">
                      <div className="stx-camp-card__robo-head">
                        <div className="stx-camp-card__eye" />
                        <div className="stx-camp-card__eye" />
                      </div>
                      <div className="stx-camp-card__robo-body" />
                    </div>
                  </div>
                  <div className="stx-camp-card__body">
                    <div className="stx-camp-card__row">
                      <span className="stx-camp-card__key">Who</span>
                      <span className="stx-camp-card__val">Ages 4 – 17</span>
                    </div>
                    <div className="stx-camp-card__row">
                      <span className="stx-camp-card__key">What</span>
                      <span className="stx-camp-card__val">Design · Build · Code</span>
                    </div>
                    <div className="stx-camp-card__row">
                      <span className="stx-camp-card__key">When</span>
                      <span className="stx-camp-card__val">School Holidays 2026</span>
                    </div>
                    <div className="stx-camp-card__row">
                      <span className="stx-camp-card__key">Where</span>
                      <span className="stx-camp-card__val">Kabarnet road off Ngong road, Nairobi, Kenya</span>
                    </div>
                  </div>
                  <Link to="/join-us" className="stx-camp-card__cta">
                    Register Now <FaArrowRight />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── PROGRAMMES ── */}
        {/* <section className="stx-section stx-programmes">
          <div className="stx-container">
            <div className="stx-section-header">
              <span className="stx-eyebrow">What We Teach</span>
              <h2 className="stx-section-title">Two Programmes. One Mission.</h2>
              <p className="stx-section-sub">Curriculum designed for schools, delivered by trained instructors, built around real problem-solving.</p>
            </div>
            <div className="stx-programmes__grid">
              <div className="stx-prog-card stx-prog-card--dark">
                <div className="stx-prog-card__icon"><FaRocket /></div>
                <h3 className="stx-prog-card__title">Robotics</h3>
                <p className="stx-prog-card__text">
                  Students design, build, and program robots — from beginner kits to competition-ready machines. Covers engineering, teamwork, and creative problem-solving.
                </p>
                <ul className="stx-prog-card__list">
                  <li>Building &amp; mechanical design</li>
                  <li>Block-based &amp; sensor programming</li>
                  <li>Competition preparation</li>
                  <li>Communication &amp; presentation</li>
                </ul>
                <Link to="/services" className="stx-prog-card__link">Explore Robotics <FaArrowRight /></Link>
              </div>
              <div className="stx-prog-card stx-prog-card--light">
                <div className="stx-prog-card__icon"><FaCode /></div>
                <h3 className="stx-prog-card__title">Python Coding</h3>
                <p className="stx-prog-card__text">
                  A structured Python curriculum that takes students from absolute beginner to building real projects — all through hands-on, project-based lessons.
                </p>
                <ul className="stx-prog-card__list">
                  <li>Core programming concepts</li>
                  <li>Logic &amp; problem-solving</li>
                  <li>Mini-projects &amp; challenges</li>
                  <li>Peer collaboration</li>
                </ul>
                <Link to="/service" className="stx-prog-card__link">Explore Coding <FaArrowRight /></Link>
              </div>
            </div>
          </div>
        </section> */}

        {/* ── WHY STEMTRIX ── */}
        <section className="stx-section stx-why">
          <div className="stx-container">
            <div className="stx-section-header">
              <span className="stx-eyebrow">Why STEMTRIX</span>
              <h2 className="stx-section-title">Built for Schools. Loved by Kids.</h2>
            </div>
            <div className="stx-why__grid">
              {[
                { icon: <FaRocket />, title: 'Hands-On Learning',       desc: 'Every lesson is built around doing — not watching. Students build, break, fix, and build again.' },
                { icon: <FaCode />,   title: 'School-Ready Curriculum', desc: 'Structured lesson plans, instructor guides, and assessments that fit seamlessly into the school calendar.' },
                { icon: <FaTrophy />, title: 'Competition Pathways',    desc: 'We prepare students for local and international competitions, including Robofest and Inspire Robotics.' },
                { icon: <FaUsers />,  title: 'Trained Instructors',     desc: 'Our educators are passionate STEM specialists who know how to connect with and inspire young learners.' },
              ].map((item, i) => (
                <div key={i} className="stx-why-card">
                  <div className="stx-why-card__icon">{item.icon}</div>
                  <h4 className="stx-why-card__title">{item.title}</h4>
                  <p className="stx-why-card__desc">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── KITS ── */}
        <section className="stx-section stx-kits">
          <div className="stx-container stx-kits__inner">
            <div className="stx-kits__content">
              <span className="stx-eyebrow">Robotics Kits</span>
              <h2 className="stx-section-title">Premium Kits for Every Level</h2>
              <p className="stx-kits__text">
                Our curated robotics kits grow with your child — from first-time builders to seasoned competitors. Every kit includes comprehensive guides and competition-ready components.
              </p>
              <div className="stx-kits__levels">
                {[
                  { label: 'Explorer',           ages: 'Ages 4–9',  color: 'a' },
                  { label: 'The Challenge',       ages: 'Ages 10–13',color: 'b' },
                  { label: 'Advanced Innovator',  ages: 'Ages 14+',  color: 'c' },
                ].map((kit, i) => (
                  <div key={i} className={`stx-kit-badge stx-kit-badge--${kit.color}`}>
                    <span className="stx-kit-badge__label">{kit.label}</span>
                    <span className="stx-kit-badge__ages">{kit.ages}</span>
                  </div>
                ))}
              </div>
              <Link to="/products" className="stx-btn stx-btn--red">View All Kits <FaArrowRight /></Link>
            </div>
            <div className="stx-kits__visual" aria-hidden="true">
              <div className="stx-kits__tower">
                <div className="stx-kits__block stx-kits__block--3"><span>Advanced</span></div>
                <div className="stx-kits__block stx-kits__block--2"><span>Intermediate</span></div>
                <div className="stx-kits__block stx-kits__block--1"><span>Beginner</span></div>
              </div>
            </div>
          </div>
        </section>

        {/* ── TESTIMONIALS ── */}
        <section className="stx-section stx-testimonials">
          <div className="stx-container">
            <div className="stx-section-header">
              <span className="stx-eyebrow">Community Voices</span>
              <h2 className="stx-section-title">What Schools &amp; Parents Say</h2>
            </div>
            <div className="stx-testimonials__grid">
              {testimonials.map((t) => (
                <div key={t.id} className="stx-testimonial">
                  <div className="stx-testimonial__quote-mark">"</div>
                  <p className="stx-testimonial__text">{t.text}</p>
                  <div className="stx-testimonial__author">
                    <div className="stx-testimonial__avatar">{t.initial}</div>
                    <div>
                      <p className="stx-testimonial__name">{t.name}</p>
                      <p className="stx-testimonial__role">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── NEWS ── */}
        <section className="stx-section stx-news">
          <div className="stx-container">
            <div className="stx-section-header">
              <span className="stx-eyebrow">Latest News</span>
              <h2 className="stx-section-title">Events, Competitions &amp; Stories</h2>
            </div>
            <div className="stx-news__grid">
              {news.map((item) => (
                <article key={item.id} className="stx-news-card">
                  <div className="stx-news-card__video">
                    <iframe src={item.video} title={item.title} allowFullScreen loading="lazy" />
                  </div>
                  <div className="stx-news-card__body">
                    <time className="stx-news-card__date">{item.date}</time>
                    <h3 className="stx-news-card__title">{item.title}</h3>
                    <p className="stx-news-card__excerpt">{item.excerpt}</p>
                   
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA BANNER ── */}
        <section className="stx-cta-banner">
          <div className="stx-container stx-cta-banner__inner">
            <div className="stx-cta-banner__text">
              <h2 className="stx-cta-banner__title">Ready to bring STEMTRIX to your school?</h2>
              <p className="stx-cta-banner__sub">
                Whether you're a school principal, a parent, or a curious student — we'd love to hear from you.
              </p>
            </div>
            <div className="stx-cta-banner__actions">
              <Link to="/join-us" className="stx-btn stx-btn--white">
                Get Started <FaArrowRight />
              </Link>
              <Link to="/contact" className="stx-btn stx-btn--outline-white">
                Contact Us
              </Link>
            </div>
          </div>
        </section>

      </main>

      <a
        href="https://wa.me/+254728128353?text=Hello!%20I%20found%20your%20contact%20on%20the%20Stemtrix%20website%20and%20would%20like%20to%20learn%20more."
        className={`stx-whatsapp ${whatsappVisible ? 'stx-whatsapp--visible' : ''}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with us on WhatsApp"
      >
        <FaWhatsapp size={24} />
        <span className="stx-whatsapp__label">Chat with us</span>
      </a>
    </>
  );
};

export default HomePage;